import {
	alwaysRefetchApiQueryOptions,
	formatCurrencyInputAmount,
	type IUseWalletBalance,
	type walletBalanceType,
} from 'BreetHelpers';
import {
	openModal,
	selectIs2faAlert,
	setIs2faAlert,
	useAppDispatch,
	useAppSelector,
	useGetUserPreferenceQuery,
	useGetUserWalletsQuery,
	useSuggest2faQuery,
} from 'BreetRedux';
import { useCallback } from 'react';

export const useWalletBalance = (): IUseWalletBalance => {
	const {
		data,
		isLoading: isWalletBalanceLoading,
		isFetching,
		isError,
	} = useGetUserWalletsQuery(undefined, alwaysRefetchApiQueryOptions);

	const { data: suggest2faQueryData, isLoading: isSuggest2faLoading } = useSuggest2faQuery();

	const wallets = data?.data;

	const getFiatWalletByType = useCallback(
		(type: walletBalanceType) => wallets?.find((w) => w.type === 'fiat' && w.currency === type.toLowerCase()),
		[wallets]
	);

	const is2faAlert = useAppSelector(selectIs2faAlert);

	const dispatch = useAppDispatch();

	const { data: userPreferenceData, isLoading: userPreferenceLoading } = useGetUserPreferenceQuery(
		undefined,
		alwaysRefetchApiQueryOptions
	);

	const trigger2faWarning = useCallback(() => {
		if (suggest2faQueryData?.data?.showSuggestion) {
			dispatch(setIs2faAlert(true));
			dispatch(openModal({ id: 'twoFAWarning', hideCloseButton: true }));
		}
	}, [dispatch, suggest2faQueryData]);

	const is2faEnabled = !!userPreferenceData?.data?.is2FAActive || !!userPreferenceData?.data?.isAuthenticator2FaActive;

	if (!isSuggest2faLoading && !userPreferenceLoading && !is2faEnabled && !is2faAlert) trigger2faWarning();

	const getBalance = useCallback(
		(fiat: walletBalanceType) => {
			const fiatWallet = getFiatWalletByType(fiat);

			return formatCurrencyInputAmount(fiatWallet?.balance ?? '0', fiat);
		},
		[getFiatWalletByType]
	);

	return {
		getFiatWalletByType,
		getBalance,
		isWalletBalanceLoading,
		isWalletBalanceFetching: isFetching,
		isWalletBalanceError: isError,
		wallets,
	};
};
